import { roundTo } from "./tillHelpers";
import { logSequenceEvent } from "./sequenceAudit";

/**
 * Data Integrity assertion utilities for POS order processing.
 *
 * These functions validate cart/order consistency before persisting or syncing.
 * They LOG warnings for minor mismatches (< 1.00) and BLOCK for critical ones (> 1.00).
 */

const TOLERANCE = 0.01;
const BLOCK_THRESHOLD = 1.00;

/**
 * DI-01: Validate cart.total matches sum(items.nettotal) + tax + charges.
 *
 * @param {Object} cart - Cart object with items, total, tax, and charge fields
 * @param {string} [context=''] - Caller context for log messages
 * @param {Object} [dbAdapter=null] - When provided, sub-₹1 (DI-01-warn) mismatches are
 *   also written to the durable sequenceAuditLog trail (see logIntegrityViolation),
 *   not just console — BUG-1965: these were previously console-only and invisible
 *   outside a live DevTools session, so nobody could review which sales had a
 *   small unresolved header/line drift. Passing dbAdapter does not change which
 *   mismatches are blocked vs warned; BLOCK_THRESHOLD/TOLERANCE are unchanged.
 * @returns {{ valid: boolean, diff: number, message?: string }}
 */
export function assertOrderTotals(cart, context = "", dbAdapter = null) {
  // A deleted line is kept in cart.items (weight zeroed, sometimes isCanceled="Y") for
  // history rather than spliced out (see cart.js's own total-computation loop, which
  // already excludes these same lines) -- summing it here counted a price the cart no
  // longer charges for, producing a false mismatch that blocked otherwise-valid saves.
  const lineItemSum = (cart.items || []).reduce(
    (s, i) => (i?.isCanceled === "Y" || i?.weight === 0 ? s : s + (Number(i.nettotal) || 0)),
    0
  );
  // Cart charge fields — note the typo "deiveryCharge" in the cart schema
  const charges =
    (Number(cart.serviceCharge) || 0) +
    (Number(cart.tip) || 0) +
    (Number(cart.deiveryCharge) || 0) +
    (Number(cart.packingCharge) || 0) +
    (Number(cart.others) || 0) +
    (Number(cart.tcs) || 0);
  // nettotal already includes tax (both tax-inclusive and tax-exclusive modes),
  // so do NOT add cart.tax again — that would double-count.
  // roundOff = roundedTotal - grossTotal (applyCurrencyRounding's convention — positive means
  // rounded UP, customer pays more), so cart.total = lineItemSum + charges + roundOff. Add it,
  // don't subtract — subtracting doubled the apparent drift (2x roundOff) and falsely blocked
  // any sale whose round-off exceeded the 1.00 threshold (e.g. roundOff=0.99 -> diff=1.98).
  const roundOff = Number(cart.roundOff) || 0;
  const expectedTotal = lineItemSum + charges + roundOff;
  const cartTotal = Number(cart.total) || 0;
  const diff = Math.abs(cartTotal - expectedTotal);

  if (diff > BLOCK_THRESHOLD) {
    logIntegrityViolation(
      "DI-01",
      {
        context,
        cartTotal,
        expectedTotal,
        diff: roundTo(diff, 4),
        lineItemSum: roundTo(lineItemSum, 4),
        tax: cart.tax,
        charges: roundTo(charges, 4),
      },
      dbAdapter
    );
    return {
      valid: false,
      diff: roundTo(diff, 4),
      message: `Order total mismatch: cart.total=${cartTotal}, expected=${roundTo(expectedTotal, 4)}, diff=${roundTo(diff, 4)}`,
    };
  }
  if (diff > TOLERANCE) {
    // BUG-1965: previously console-only, so a real sub-₹1 mismatch (typical of
    // rounding/tax-split drift) saved with no durable trace anyone could review.
    // Reuses the existing sequenceAuditLog trail (same mechanism as DI-31/DI-32/
    // DI-33) — visible via window.__seqDiag()/__seqDiagDownload() and
    // getSequenceAuditTrail(db), no new storage/plumbing introduced. Does NOT
    // change save behavior: still non-blocking, same TOLERANCE/BLOCK_THRESHOLD.
    logIntegrityViolation(
      "DI-01-warn",
      {
        context,
        cartTotal,
        expectedTotal: roundTo(expectedTotal, 4),
        diff: roundTo(diff, 4),
        documentno: cart.documentno,
        sOrderID: cart.sOrderID,
      },
      dbAdapter
    );
  }
  return { valid: true, diff: roundTo(diff, 4) };
}

/**
 * BUG-1965 (full fix): self-heal a sub-₹1 header/line drift by nudging ONE line
 * item's nettotal to close the gap — BEFORE any of item.nettotal's derived fields
 * (linenet/linetax/taxDetails/combinedTaxRates — see the per-item transform loop
 * in useOrderProcessing.js, right after this function's call site) are computed
 * from it, so those derived fields come out consistent with the corrected value
 * instead of needing a separate re-derivation.
 *
 * Deliberately corrects the LINE side, never cart.total: cart.total already
 * equals what was actually collected from the customer (assertPaymentBalance/
 * DI-02 validates payments against cart.total earlier in the same checkout flow,
 * before this function ever runs), so changing cart.total here would trade a
 * header/line mismatch for a header/payment mismatch instead of eliminating one.
 *
 * Only the sub-₹1 warn band (TOLERANCE < diff <= BLOCK_THRESHOLD) is self-healed.
 * Diffs above BLOCK_THRESHOLD are left alone — assertOrderTotals' existing
 * block-and-refuse-to-save path (called separately, unchanged) still owns those,
 * since a diff that large is worth investigating rather than silently patched.
 *
 * @param {Object} cart - cartToDb, BEFORE the per-item tax-detail transform loop runs
 * @param {string} [context=''] - Caller context for log messages
 * @param {Object} [dbAdapter=null] - When provided, the correction is durably logged
 * @returns {{ corrected: boolean, itemIndex?: number, adjustment?: number }}
 */
export function reconcileOrderTotalDrift(cart, context = "", dbAdapter = null) {
  const items = cart.items || [];
  if (items.length === 0) return { corrected: false };

  // Same dead-line exclusion as assertOrderTotals above -- without it, a leftover
  // canceled/weight-0 line's nettotal masquerades as real drift, and this function
  // would "correct" it by silently mutating a genuine line's price to compensate.
  const lineItemSum = items.reduce((s, i) => (i?.isCanceled === "Y" || i?.weight === 0 ? s : s + (Number(i.nettotal) || 0)), 0);
  const charges =
    (Number(cart.serviceCharge) || 0) +
    (Number(cart.tip) || 0) +
    (Number(cart.deiveryCharge) || 0) +
    (Number(cart.packingCharge) || 0) +
    (Number(cart.others) || 0) +
    (Number(cart.tcs) || 0);
  const roundOff = Number(cart.roundOff) || 0;
  const expectedTotal = lineItemSum + charges + roundOff;
  const cartTotal = Number(cart.total) || 0;
  const diff = Math.abs(cartTotal - expectedTotal);

  if (diff <= TOLERANCE || diff > BLOCK_THRESHOLD) return { corrected: false };

  const adjustment = roundTo(cartTotal - expectedTotal, 2);

  // Prefer the last non-gift-card item whose |nettotal| comfortably exceeds
  // the adjustment — gift card lines carry special monetary-instrument
  // handling and are skipped by the per-item tax-detail transform
  // (`!item.isGiftCard` gate below), so absorbing the drift there wouldn't
  // even be reflected in linenet/taxDetails. The magnitude check guards a
  // real edge case: a very low-priced last item (e.g. a ₹0.50 candy line)
  // could otherwise have its sign flipped or be zeroed out by an adjustment
  // up to ₹1.00, producing a nonsensical negative/zero line on the receipt.
  // If no item can safely absorb it, leave the cart untouched — the diff
  // still gets recorded via assertOrderTotals' existing DI-01-warn logging
  // (unchanged, called separately), same as before this fix existed.
  // BUG-2043 (reopened): a grouped / Custom Mix parent is not itself an exported line — the
  // invoice carries its CHILDREN. Absorbing the drift into the parent alone would leave the
  // children summing to the pre-correction figure, i.e. it would re-open the very header/line
  // gap this ticket closes, just at sub-₹1 scale. So a grouped parent is only a valid candidate
  // if one of its children can absorb the adjustment too, and the correction is applied to both.
  let idx = -1;
  let childIdx = -1;
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].isGiftCard || items[i].isCanceled === "Y" || items[i].weight === 0) continue;
    const nt = Number(items[i].nettotal) || 0;
    if (Math.abs(nt) <= Math.abs(adjustment)) continue;

    const kids = items[i].children;
    if (Array.isArray(kids) && kids.length > 0) {
      let biggest = -1;
      for (let c = 0; c < kids.length; c++) {
        if (kids[c].isGiftCard) continue;
        const cnt = Math.abs(Number(kids[c].nettotal) || 0);
        if (cnt > Math.abs(adjustment) && (biggest === -1 || cnt > Math.abs(Number(kids[biggest].nettotal) || 0))) {
          biggest = c;
        }
      }
      if (biggest === -1) continue;
      childIdx = biggest;
    }

    idx = i;
    break;
  }
  if (idx === -1) return { corrected: false };

  const before = roundTo(Number(items[idx].nettotal) || 0, 2);
  const after = roundTo(before + adjustment, 2);
  items[idx].nettotal = after;

  // BUG: nettotal alone feeds linenet/linetax/taxDetails (all correctly re-derived
  // from it in the transform loop right after this call — see this function's
  // docblock), but in tax-EXCLUSIVE mode the printed per-unit price is instead
  // read straight from item.salePrice, a field this correction never touched —
  // so the receipt's Price (salePrice) x Qty could stop matching the corrected
  // Amount for this one line. Nudge salePrice by the same per-unit share of the
  // adjustment so the printed unit price stays consistent with the corrected total.
  const weight = Number(items[idx].weight) || 1;
  if (Number.isFinite(Number(items[idx].salePrice)) && weight !== 0) {
    items[idx].salePrice = roundTo((Number(items[idx].salePrice) || 0) + adjustment / weight, 4);
  }

  // BUG-2043 (reopened): mirror the correction onto the chosen child so the exported lines still
  // sum to the corrected parent. The child's line fields are re-derived from this nettotal by the
  // children pass in useOrderProcessing's transform loop, which runs immediately after this call.
  if (childIdx !== -1) {
    const child = items[idx].children[childIdx];
    child.nettotal = roundTo((Number(child.nettotal) || 0) + adjustment, 2);
    const childWeight = Number(child.weight) || 1;
    if (Number.isFinite(Number(child.salePrice)) && childWeight !== 0) {
      child.salePrice = roundTo((Number(child.salePrice) || 0) + adjustment / childWeight, 4);
    }
  }

  logIntegrityViolation(
    "DI-01-corrected",
    {
      context,
      documentno: cart.documentno,
      sOrderID: cart.sOrderID,
      cartTotal,
      expectedTotal: roundTo(expectedTotal, 4),
      diff: roundTo(diff, 4),
      correctedItemIndex: idx,
      correctedItemBefore: before,
      correctedItemAfter: after,
      adjustment,
    },
    dbAdapter
  );

  return { corrected: true, itemIndex: idx, adjustment };
}

/**
 * DI-02: Validate sum(cart.payments) matches cart.total.
 *
 * For return orders (cart.isReturn), payments may still be positive at
 * validation time (negated later in processOrderApproved), so we compare
 * absolute values. For exchange orders, caller should pass the activeCart.
 *
 * @param {Object} cart - Cart object with payments array and total
 * @param {string} [context=''] - Caller context for log messages
 * @returns {{ valid: boolean, diff: number, message?: string }}
 */
export function assertPaymentBalance(cart, context = "") {
  const paymentSum = (cart.payments || []).reduce(
    (s, p) => s + (Number(p.amount) || 0),
    0
  );
  const isReturn = cart.isReturn === true || cart.isReturn === "Y";
  const cartTotal = Number(cart.total) || 0;
  // For returns, payments may not yet be negated at validation time —
  // compare absolute values so sign convention doesn't cause false failures
  const diff = isReturn
    ? Math.abs(Math.abs(paymentSum) - Math.abs(cartTotal))
    : Math.abs(paymentSum - cartTotal);

  if (diff > BLOCK_THRESHOLD) {
    logIntegrityViolation("DI-02", {
      context,
      paymentSum: roundTo(paymentSum, 4),
      cartTotal: roundTo(cartTotal, 4),
      diff: roundTo(diff, 4),
      paymentCount: cart.payments?.length || 0,
      isReturn,
    });
    return {
      valid: false,
      diff: roundTo(diff, 4),
      message: `Payment total mismatch: payments=${roundTo(paymentSum, 4)}, cartTotal=${roundTo(cartTotal, 4)}, diff=${roundTo(diff, 4)}`,
    };
  }
  if (diff > TOLERANCE) {
    logIntegrityViolation("DI-02-warn", {
      context,
      paymentSum: roundTo(paymentSum, 4),
      cartTotal: roundTo(cartTotal, 4),
      diff: roundTo(diff, 4),
    });
  }
  return { valid: true, diff: roundTo(diff, 4) };
}

/**
 * DI-03: Validate order JSON self-consistency: nettotal + taxamt + tcs === orderTotal.
 *
 * If mismatch is detected, returns a corrected nettotal derived from orderTotal.
 *
 * @param {Object} payload - Object with nettotal, taxamt, tcs (or charges), and orderTotal
 * @param {string} [context=''] - Caller context
 * @returns {{ valid: boolean, diff: number, correctedNettotal?: number }}
 */
export function assertOrderJSON(payload, context = "") {
  const nettotal = Number(payload.nettotal) || 0;
  const taxamt = Number(payload.taxamt) || 0;
  const tcs = Number(payload.tcs) || 0;
  const orderTotal = Number(payload.orderTotal) || 0;
  const reconstructed = nettotal + taxamt + tcs;
  const diff = Math.abs(reconstructed - orderTotal);

  if (diff > TOLERANCE) {
    const correctedNettotal = orderTotal - taxamt - tcs;
    logIntegrityViolation("DI-03", {
      context,
      originalNettotal: roundTo(nettotal, 4),
      correctedNettotal: roundTo(correctedNettotal, 4),
      taxamt: roundTo(taxamt, 4),
      tcs: roundTo(tcs, 4),
      orderTotal: roundTo(orderTotal, 4),
      reconstructed: roundTo(reconstructed, 4),
      diff: roundTo(diff, 4),
    });
    return {
      valid: false,
      diff: roundTo(diff, 4),
      correctedNettotal: roundTo(correctedNettotal, 4),
    };
  }
  return { valid: true, diff: roundTo(diff, 4) };
}

/**
 * DI-34: Validate the outbound `line` array (orderLines) sums to the header
 * total in the SAME payload — DI-03's blind spot. DI-03 only checks header
 * self-consistency (nettotal+taxamt+tcs===orderTotal); it has no visibility
 * into the actual `line` array being sent, so if a line item is excluded from
 * that array downstream of wherever the header total was computed (e.g. a
 * cancelled-item skip in the orderLines-building loop, or any other future
 * filter), the header still reflects the original total while the backend
 * receives fewer lines than that total accounts for. Confirmed production
 * case: TICKET-6569, doc A/2627/22516 — orderTotal/nettotal/taxamt reflected
 * 3 line items' worth of value, but only 2 were present in the outbound
 * `line` array.
 *
 * Non-blocking, matching DI-01/DI-03's philosophy — a false positive here
 * would strand an otherwise-valid, already-paid sale unsynced, which is worse
 * than a reviewable log entry.
 *
 * Self-heal (added alongside the Dadus/Zomato taxamt=0 investigation,
 * confirmed via direct DB query — headerNet matches lineNetSum exactly while
 * headerTax is written as a flat 0 despite every line carrying real tax):
 * ONLY when net already matches (netDiff within tolerance) but tax doesn't
 * do we return `correctedTaxamt` derived from the line array. That net-match
 * is deliberately the gate — it is the one signal that distinguishes "the
 * header tax scalar alone is corrupted, the line array is trustworthy" (this
 * bug) from TICKET-6569's "a line got dropped" case, where net would ALSO be
 * off and self-healing from an incomplete line array would compound the
 * error instead of fixing it. When both net and tax disagree, no correction
 * is offered — that's still TICKET-6569's territory and stays log-only.
 *
 * @param {Object} orderJson - Object with `line` (array of {linenet, linetax}), nettotal, taxamt
 * @param {string} [context=''] - Caller context for log messages
 * @param {Object} [dbAdapter=null] - When provided, mismatches are durably logged (see logIntegrityViolation)
 * @param {number} [charges=0] - Header-level charges (serviceCharge/tip/deiveryCharge/packingCharge/
 *   others) folded into nettotal by the caller but never represented as a line in `orderJson.line`
 *   (no pseudo-line is ever pushed for them). Without this, every order carrying a nonzero charge
 *   permanently fails the net comparison below even though nothing is actually wrong — the caller
 *   is expected to pass the same charge total DI-01 (assertOrderTotals) already sums.
 * @returns {{ valid: boolean, netDiff: number, taxDiff: number, correctedTaxamt?: number }}
 */
export function assertOrderLinesMatchHeader(orderJson, context = "", dbAdapter = null, charges = 0) {
  const lines = orderJson.line || [];
  const lineNetSum = lines.reduce((s, l) => s + (Number(l.linenet) || 0), 0);
  const lineTaxSum = lines.reduce((s, l) => s + (Number(l.linetax) || 0), 0);
  const headerNet = Number(orderJson.nettotal) || 0;
  const headerTax = Number(orderJson.taxamt) || 0;
  const netDiff = Math.abs(headerNet - lineNetSum - (Number(charges) || 0));
  const taxDiff = Math.abs(headerTax - lineTaxSum);

  if (netDiff > TOLERANCE || taxDiff > TOLERANCE) {
    const canSelfHealTax = netDiff <= TOLERANCE && taxDiff > TOLERANCE && lines.length > 0;
    logIntegrityViolation(
      "DI-34",
      {
        context,
        documentno: orderJson.documentno,
        sOrderID: orderJson.sOrderID,
        headerNet: roundTo(headerNet, 4),
        lineNetSum: roundTo(lineNetSum, 4),
        charges: roundTo(Number(charges) || 0, 4),
        netDiff: roundTo(netDiff, 4),
        headerTax: roundTo(headerTax, 4),
        lineTaxSum: roundTo(lineTaxSum, 4),
        taxDiff: roundTo(taxDiff, 4),
        lineCount: lines.length,
        correctedTaxamt: canSelfHealTax ? roundTo(lineTaxSum, 4) : undefined,
      },
      dbAdapter
    );
    return {
      valid: false,
      netDiff: roundTo(netDiff, 4),
      taxDiff: roundTo(taxDiff, 4),
      correctedTaxamt: canSelfHealTax ? roundTo(lineTaxSum, 4) : undefined,
    };
  }
  return { valid: true, netDiff: roundTo(netDiff, 4), taxDiff: roundTo(taxDiff, 4) };
}

/**
 * DI-35: Validate every mandatory numeric field in the final outbound order payload
 * is finite (not NaN/Infinity) before it is sent to posOrderProcessor1 /
 * confirmPOSLayawayOrder1.
 *
 * Root cause this guards against (BUG-2041/2044 follow-up, 2026-08-21): a NaN
 * quantity/nettotal anywhere upstream (cart.js) survives JSON.stringify as a
 * literal `null` (JSON.stringify(NaN) === null). Confirmed directly against
 * live KWC/GPR/Dadus Postgres: s_order.grosstotal/nettotal/taxamt and
 * s_orderline.qty are all NOT NULL DEFAULT 0 -- an explicit null bound against
 * a NOT NULL column (as opposed to the column being omitted, which is the only
 * case the DEFAULT applies) makes the INSERT fail outright, so the order never
 * reaches s_order at all and stays stuck in the POS's local pending-sync queue
 * forever with no diagnosable reason -- exactly the reported "POS pending sync
 * orders fail because mandatory fields ... contain NULL values" symptom.
 *
 * Deliberately non-blocking, matching DI-01/DI-03/DI-34's philosophy elsewhere
 * in this file: the sale has already been completed/paid by the time this runs
 * in the sync loop, so there is nothing safe to "block" here -- holding the
 * order back client-side would strand an already-paid sale exactly as badly as
 * the backend rejecting it does today. This function's entire value is
 * visibility: previously nothing logged which field, on which order/line, was
 * the actual cause of a stuck-pending order. It never mutates orderJson --
 * purely diagnostic, so it cannot introduce a new regression on a valid order.
 *
 * @param {Object} orderJson - The assembled outbound order payload
 * @param {string} [context=''] - Caller context for log messages
 * @param {Object} [dbAdapter=null] - When provided, also durably logged (see logIntegrityViolation)
 * @returns {{ valid: boolean, invalidFields: string[] }}
 */
export function assertOrderPayloadFinite(orderJson, context = "", dbAdapter = null) {
  const invalidFields = [];
  ["orderTotal", "nettotal", "taxamt"].forEach((field) => {
    if (!Number.isFinite(Number(orderJson[field]))) {
      invalidFields.push(field);
    }
  });

  const lines = orderJson.line || [];
  lines.forEach((l, idx) => {
    ["qty", "linenet", "linetax"].forEach((field) => {
      if (!Number.isFinite(Number(l[field]))) {
        invalidFields.push(`line[${idx}].${field}`);
      }
    });
  });

  if (invalidFields.length > 0) {
    logIntegrityViolation(
      "DI-35",
      {
        context,
        documentno: orderJson.documentno,
        sOrderID: orderJson.sOrderID,
        invalidFields,
      },
      dbAdapter
    );
    return { valid: false, invalidFields };
  }
  return { valid: true, invalidFields: [] };
}

/**
 * DI-07: Log adjustments made by adjustOrderTotals.
 *
 * Should be called BEFORE any silent adjustment is applied.
 *
 * @param {Object} original - { total, tax } before adjustment
 * @param {Object} adjusted - { total, tax } after adjustment
 */
export function logAdjustOrderTotals(original, adjusted) {
  const grossDiff = adjusted.total - original.total;
  const taxDiff = adjusted.tax - original.tax;
  if (Math.abs(grossDiff) > TOLERANCE || Math.abs(taxDiff) > TOLERANCE) {
    logIntegrityViolation("DI-07", {
      originalTotal: original.total,
      adjustedTotal: adjusted.total,
      grossDiff: roundTo(grossDiff, 4),
      originalTax: original.tax,
      adjustedTax: adjusted.tax,
      taxDiff: roundTo(taxDiff, 4),
    });
  }
}

/**
 * DI-15 / DI-19: Verify sync completeness after processSync.
 *
 * Checks that critical tables (products, productCategories, pricingRules) have
 * at least one record. Returns a summary object with counts and a warning flag.
 *
 * @param {Object} dbAdapter - Database adapter (Dexie or SQLite)
 * @returns {Promise<{ complete: boolean, counts: Object, warnings: string[] }>}
 */
export async function verifySyncCompleteness(dbAdapter) {
  const warnings = [];
  const counts = {};
  try {
    const products = await dbAdapter.products.count();
    const categories = await dbAdapter.productCategories.count();
    const pricingRules = await dbAdapter.pricingRules.count();
    const posSaletypes = await dbAdapter.posSaletypes.count();

    counts.products = products;
    counts.productCategories = categories;
    counts.pricingRules = pricingRules;
    counts.posSaletypes = posSaletypes;

    if (products === 0) {
      warnings.push("No products synced — POS cannot function without products");
    }
    if (categories === 0) {
      warnings.push("No product categories synced");
    }
    if (pricingRules === 0) {
      // pricingRules can legitimately be 0 for some tenants — warn but don't fail
      warnings.push("No pricing rules synced (may be expected for this tenant)");
    }
    if (posSaletypes === 0) {
      warnings.push("No POS sale types synced — sale type selection will fail");
    }
  } catch (err) {
    warnings.push(`Sync verification DB error: ${err?.message || err}`);
  }

  const complete = counts.products > 0 && counts.posSaletypes > 0;
  if (warnings.length > 0) {
    logIntegrityViolation("DI-19", { counts, warnings });
  }
  return { complete, counts, warnings };
}

/**
 * DI-28: Log gift card deduction for tracking/reconciliation.
 *
 * Called when a gift card redemption API succeeds BEFORE order confirmation.
 * This creates an audit trail so that if the order subsequently fails, the
 * deduction can be identified and manually reconciled.
 *
 * @param {Object} params
 * @param {string} params.cardNo - Gift card number
 * @param {number} params.amount - Amount deducted
 * @param {string} params.referenceId - Transaction reference ID
 * @param {string} params.sOrderID - Order ID (if available)
 * @param {string} params.tillId - Till ID
 */
export function logGiftCardDeduction({ cardNo, amount, referenceId, sOrderID, tillId }) {
  console.warn(
    `[DI-28] Gift card deducted pre-confirmation: card=${cardNo}, amount=${amount}, ref=${referenceId}, order=${sOrderID || "pending"}, till=${tillId || "unknown"}`
  );
  logIntegrityViolation("DI-28", {
    type: "gift_card_pre_deduction",
    cardNo,
    amount,
    referenceId,
    sOrderID: sOrderID || null,
    tillId: tillId || null,
    timestamp: new Date().toISOString(),
  });
}

/**
 * DI-29: Log loyalty points deduction for tracking/reconciliation.
 *
 * Same rationale as DI-28 — loyalty points are deducted before order
 * confirmation. This creates an audit trail for reconciliation.
 *
 * @param {Object} params
 * @param {string} params.customerId - Customer ID
 * @param {number} params.points - Points/amount being redeemed
 * @param {string} params.sOrderID - Order ID (if available)
 * @param {string} params.tillId - Till ID
 * @param {string} params.paymentMethodName - Payment method name
 */
export function logLoyaltyDeduction({ customerId, points, sOrderID, tillId, paymentMethodName }) {
  console.warn(
    `[DI-29] Loyalty points used pre-confirmation: customer=${customerId}, points=${points}, order=${sOrderID || "pending"}, till=${tillId || "unknown"}`
  );
  logIntegrityViolation("DI-29", {
    type: "loyalty_pre_deduction",
    customerId,
    points,
    sOrderID: sOrderID || null,
    tillId: tillId || null,
    paymentMethodName: paymentMethodName || null,
    timestamp: new Date().toISOString(),
  });
}

/**
 * DI-30: Log coupon validation with terminal context.
 *
 * Creates an audit trail showing which terminal validated a coupon and when,
 * to aid in diagnosing simultaneous-use race conditions.
 *
 * @param {Object} params
 * @param {string} params.couponCode - The coupon code being validated
 * @param {string} params.referenceId - Unique reference for this validation
 * @param {string} params.tillId - Till/terminal ID
 * @param {string} params.bunitId - Business unit ID
 */
export function logCouponValidation({ couponCode, referenceId, tillId, bunitId }) {
  logIntegrityViolation("DI-30", {
    type: "coupon_validation",
    couponCode,
    referenceId,
    tillId: tillId || null,
    bunitId: bunitId || null,
    timestamp: new Date().toISOString(),
  });
}

/**
 * DI-S1: Re-verify stock at checkout for all cart items.
 *
 * Performs a non-blocking stock check. Returns { allAvailable, warnings[] }.
 * Only checks items with type "I" or no type. Skips in offline mode.
 *
 * @param {Object} params
 * @param {Object} params.cart - Cart object
 * @param {Function} params.verifyStockService - The verifyStock API service
 * @param {Object} params.tillData - Till configuration
 * @returns {Promise<{ allAvailable: boolean, warnings: string[] }>}
 */
export async function reVerifyStockAtCheckout({ cart, verifyStockService, tillData }) {
  const warnings = [];
  if (!navigator.onLine) {
    return { allAvailable: true, warnings: ["Offline mode — stock re-check skipped"] };
  }

  const stockItems = (cart.items || []).filter(
    (item) => (item.type === "I" || !item.type) && !item.isGiftCard && !item.isReturn
  );

  if (stockItems.length === 0) {
    return { allAvailable: true, warnings: [] };
  }

  try {
    const buildCartProducts = stockItems
      .map(({ value, weight, mBatchId, csBunitId }) =>
        `{ productId: "${value}", mBatchId: ${mBatchId ? `"${mBatchId}"` : null}, orderQty: ${weight}, cSBunitID: ${csBunitId ? `"${csBunitId}"` : null} }`
      )
      .join(", ");

    const data = {
      query: `query {
        verifyStock(order: {
          orderNo: "${cart.documentno || "CHECKOUT_VERIFY"}"
          tillId: "${tillData?.tillAccess?.cwrTill?.searchKey || ""}"
          cartItems: [${buildCartProducts}]
        }) {
          cartItems {
            productId
            orderQty
            availabilityStatus
            availableQty
          }
        }
      }`,
    };

    const response = await verifyStockService(data);
    if (response?.status === 200) {
      const resultItems = response.data?.data?.verifyStock?.cartItems || [];
      for (const resItem of resultItems) {
        if (resItem.availabilityStatus !== "Yes") {
          const cartItem = stockItems.find((i) => i.value === resItem.productId);
          const name = cartItem?.name || resItem.productId;
          if (resItem.availableQty < (cartItem?.weight || 0)) {
            warnings.push(
              `${name}: requested ${cartItem?.weight}, available ${resItem.availableQty}`
            );
          }
        }
      }
    }
  } catch (err) {
    console.warn("[DI-S1] Stock re-verification failed (non-blocking):", err?.message);
    // Non-blocking — don't fail the checkout
  }

  if (warnings.length > 0) {
    logIntegrityViolation("DI-S1", {
      type: "checkout_stock_warning",
      warnings,
      timestamp: new Date().toISOString(),
    });
  }

  return { allAvailable: warnings.length === 0, warnings };
}

/**
 * DI-31: Check documentno uniqueness before saving order.
 *
 * Soft check — NEVER blocks the order. If a collision is found, auto-bumps
 * the document sequence and returns a corrected documentno. The caller
 * should use the returned documentno on the cart before persisting.
 *
 * @param {Object} params
 * @param {Object} params.dbAdapter - Database adapter (db.orders must support .where)
 * @param {string} params.documentno - The documentno about to be saved
 * @param {string} params.sOrderID - Current order's sOrderID (to exclude self-updates)
 * @param {string} params.prefix - Till prefix for regenerating documentno
 * @param {Object} params.sessionStore - useSessionStore (for reading/bumping sequence)
 * @returns {Promise<{ unique: boolean, documentno: string, recovered?: boolean, recoveredSeq?: number }>}
 */
export async function assertDocumentNoUnique({ dbAdapter, documentno, sOrderID, prefix, sessionStore, sequenceType = "regular" }) {
  try {
    if (!documentno) return { unique: true, documentno };

    const existing = await dbAdapter.orders.where("documentno").equals(documentno).toArray();
    const collision = existing.find((o) => o.sOrderID !== sOrderID);

    if (!collision) return { unique: true, documentno };

    // Collision found — auto-recover by finding max sequence and bumping past it
    const allOrders = await dbAdapter.orders.toArray();
    let maxSeq = 0;
    for (const order of allOrders) {
      if (order.documentno && order.documentno.startsWith(prefix)) {
        const seqPart = parseInt(order.documentno.slice(prefix.length), 10);
        if (!isNaN(seqPart) && seqPart > maxSeq) maxSeq = seqPart;
      }
    }
    const recoveredSeq = maxSeq + 1;
    let suffix = "";
    if (sequenceType === "preorder") {
      suffix = sessionStore?.getState?.()?.preorderSequence?.suffix || "";
    } else if (sequenceType === "cancelled") {
      suffix = sessionStore?.getState?.()?.cancelledOrderSequence?.suffix || "";
    }
    const recoveredDocNo = `${prefix}${recoveredSeq}${suffix}`;

    if (sessionStore?.getState) {
      if (sequenceType === "preorder") {
        const current = sessionStore.getState().preorderSequence;
        if (current) sessionStore.getState().setPreorderSequence({ ...current, nextNo: recoveredSeq });
      } else if (sequenceType === "cancelled") {
        const current = sessionStore.getState().cancelledOrderSequence;
        if (current) sessionStore.getState().setCancelledOrderSequence({ ...current, nextNo: recoveredSeq });
      } else {
        sessionStore.getState().setDocumentSequence(recoveredSeq);
      }
    }

    logIntegrityViolation("DI-31", {
      type: "duplicate_documentno_recovered",
      originalDocNo: documentno,
      recoveredDocNo,
      collidedWithSOrderID: collision.sOrderID,
      recoveredSeq,
      timestamp: new Date().toISOString(),
    });
    logSequenceEvent(
      "di31-recovery",
      {
        documentno: recoveredDocNo,
        originalDocNo: documentno,
        collidedWithSOrderID: collision.sOrderID,
        maxSeq,
        recoveredSeq,
        sequenceType,
        prefix,
      },
      dbAdapter
    );

    return { unique: false, documentno: recoveredDocNo, recovered: true, recoveredSeq };
  } catch (err) {
    // NEVER block — if the check itself fails, let the order through with original documentno
    console.warn("[DI-31] Document number uniqueness check failed (non-blocking):", err?.message);
    return { unique: true, documentno };
  }
}

/**
 * DI-32: Recover orders stuck in isSynced=3 (syncing) after crash/reload.
 *
 * On app start, orders left in isSynced=3 from a previous session are stuck
 * forever — the sync interval only picks up 0 and 2. This resets stale ones
 * back to 0 so they get retried.
 *
 * @param {Object} dbAdapter - Database adapter (db.orders)
 * @param {number} [staleMinutes=2] - Minutes after which isSynced=3 is considered stale
 * @returns {Promise<{ recovered: number, orders: string[] }>}
 */
export async function recoverStuckSyncOrders(dbAdapter, staleMinutes = 2) {
  try {
    const stuckOrders = await dbAdapter.orders.where("isSynced").equals(3).toArray();
    if (stuckOrders.length === 0) return { recovered: 0, orders: [] };

    const now = Date.now();
    const staleThreshold = staleMinutes * 60 * 1000;
    const recovered = [];

    for (const order of stuckOrders) {
      const lastAttempt = order.lastSyncAttempt ? new Date(order.lastSyncAttempt).getTime() : 0;
      const age = now - lastAttempt;

      if (age > staleThreshold || !order.lastSyncAttempt) {
        // BUG-1966: re-check current state right before resetting. The initial
        // stuckOrders snapshot above can go stale — the original posOrderProcessor1
        // call for this same order may finally resolve and flip isSynced to 1
        // (via its own success handler) in the time between that snapshot and this
        // write, since both involve awaited I/O and the event loop can interleave
        // them. Resetting a just-completed order back to 0 here would re-queue it
        // for a duplicate resend, re-submitting its embedded `payments` array a
        // second time (production incidents: BHRS/2160, BHRS/2144, BHRS/2143,
        // BHRS/2165, BHRS/2147, WP/102588, HMN/102484, KDR/105064, TMK/103608).
        // This does not close the case where the network response was lost AFTER
        // the backend already committed the order — that requires the backend's
        // payment-line insert to be idempotent on orderId, which is outside what
        // this client-side check can verify or guarantee.
        const current = await dbAdapter.orders.where("sOrderID").equals(order.sOrderID).first();
        if (!current || (current.isSynced !== 3 && current.isSynced !== "3")) continue;
        await dbAdapter.orders.where("sOrderID").equals(order.sOrderID).modify({ isSynced: 0 });
        recovered.push(order.sOrderID);
      }
    }

    if (recovered.length > 0) {
      logIntegrityViolation("DI-32", {
        type: "stuck_sync_recovery",
        recoveredCount: recovered.length,
        sOrderIDs: recovered,
        staleMinutes,
        timestamp: new Date().toISOString(),
      });
    }

    return { recovered: recovered.length, orders: recovered };
  } catch (err) {
    console.warn("[DI-32] Stuck sync recovery failed (non-blocking):", err?.message);
    return { recovered: 0, orders: [] };
  }
}

/**
 * DI-36: Verify mirror-written orders (useWebSocketHandler.js POS_ORDER_UPSERT)
 * against the server before ever letting them enter the blind-create sync loop.
 *
 * posOrderProcessor1 is a blind CREATE, not idempotent by sOrderID (production
 * trace: a cancelled/resubmitted order came back under a brand-new document
 * number, BHRS/1781 -> BHRS/1785 -- see the isPrepareBillCancelled comment in
 * useOrderProcessing.js). Calling it twice for the same logical order mints two
 * separate server documents, not a dedup/no-op. A mirror device (a Sales POS
 * that received another device's order over the websocket, rather than
 * originating it) must therefore never call it speculatively.
 *
 * Mirror orders are written with isSynced: 1 (excluded from the normal
 * isSynced === 0 pending-sync loop) plus mirrorOrder: true / mirrorVerifyAfter /
 * mirrorVerified: false. Once the grace period has elapsed -- giving the actual
 * owning device's own sync a fair chance to complete first -- this checks the
 * real, read-only salesHistoryDetails(orderId) lookup to see whether the order
 * already reached the server:
 *   - found on server -> already synced by the owner; mark mirrorVerified,
 *                        leave isSynced: 1.
 *   - not found        -> the owner never synced it; promote to isSynced: 0 so
 *                        the normal pending-sync loop safely creates it for real.
 *   - lookup failed     -> retried on the next auto tick, up to
 *                        MIRROR_VERIFY_MAX_ATTEMPTS. A device with a persistent
 *                        problem reaching salesHistoryDetails (bad auth scope,
 *                        chronic network issue affecting only this call) would
 *                        otherwise retry this forever with the order stuck at
 *                        isSynced: 1 -- invisible to the normal pending-sync
 *                        loop AND to its syncAttempts/notifySyncFailure ceiling,
 *                        silently reproducing the exact "order never reaches
 *                        the server, no error anywhere" bug this mechanism
 *                        exists to fix, via a different door. Once exhausted,
 *                        this fails open (promotes to isSynced: 0 anyway) and
 *                        reports the order in the returned `stuck` array so the
 *                        caller can raise a visible, operator-facing alert
 *                        (see notifySyncFailure, useOrderProcessing.js) --
 *                        permanent invisibility is worse than the rare
 *                        duplicate this design otherwise avoids.
 *
 * @param {Object} dbAdapter - Database adapter (db.orders)
 * @param {Function} existsOnServerFn - async (sOrderID) => true | false | null.
 *   null means "lookup failed, unknown" -- injected so this file stays free of
 *   network/service imports and so tests can mock it directly.
 * @returns {Promise<{ verified: number, promoted: number, stuck: Array<{sOrderID: string, documentno: string, attempts: number}> }>}
 */
const MIRROR_VERIFY_MAX_ATTEMPTS = 90; // ~15 min at the 10s auto-sync tick cadence

export async function verifyMirrorOrders(dbAdapter, existsOnServerFn) {
  try {
    const allOrders = await dbAdapter.orders.toArray();
    const now = Date.now();
    const pending = allOrders.filter(
      (o) => o.mirrorOrder && !o.mirrorVerified && o.mirrorVerifyAfter && new Date(o.mirrorVerifyAfter).getTime() <= now
    );
    if (pending.length === 0) return { verified: 0, promoted: 0, stuck: [] };

    let verified = 0;
    let promoted = 0;
    const stuck = [];

    for (const order of pending) {
      try {
        const existsOnServer = await existsOnServerFn(order.sOrderID);

        if (existsOnServer === null || existsOnServer === undefined) {
          // lookup failed — retry next tick, up to MIRROR_VERIFY_MAX_ATTEMPTS
          const attempts = (order.mirrorVerifyAttempts || 0) + 1;
          if (attempts >= MIRROR_VERIFY_MAX_ATTEMPTS) {
            await dbAdapter.orders
              .where("sOrderID")
              .equals(order.sOrderID)
              .modify({ mirrorVerified: true, isSynced: 0, mirrorVerifyAttempts: attempts });
            promoted += 1;
            stuck.push({ sOrderID: order.sOrderID, documentno: order.documentno, attempts });
            logIntegrityViolation(
              "DI-36",
              {
                type: "mirror_order_verify_exhausted_failing_open",
                sOrderID: order.sOrderID,
                documentno: order.documentno,
                attempts,
                timestamp: new Date().toISOString(),
              },
              dbAdapter
            );
          } else {
            await dbAdapter.orders.where("sOrderID").equals(order.sOrderID).modify({ mirrorVerifyAttempts: attempts });
          }
          continue;
        }

        if (existsOnServer) {
          await dbAdapter.orders.where("sOrderID").equals(order.sOrderID).modify({ mirrorVerified: true });
          verified += 1;
        } else {
          await dbAdapter.orders.where("sOrderID").equals(order.sOrderID).modify({ mirrorVerified: true, isSynced: 0 });
          promoted += 1;
          logIntegrityViolation(
            "DI-36",
            {
              type: "mirror_order_promoted_to_sync",
              sOrderID: order.sOrderID,
              documentno: order.documentno,
              timestamp: new Date().toISOString(),
            },
            dbAdapter
          );
        }
      } catch (err) {
        console.warn("[DI-36] Verify failed for order (non-blocking, retried next tick):", order.sOrderID, err?.message);
      }
    }

    return { verified, promoted, stuck };
  } catch (err) {
    console.warn("[DI-36] Mirror order verification failed (non-blocking):", err?.message);
    return { verified: 0, promoted: 0, stuck: [] };
  }
}

/**
 * DI-37: Detect a decimal/weight line whose `nettotal` (charged amount — feeds
 * subTotal/tax/total) has drifted from `linestd` (weight-scaled standard total,
 * recomputed fresh from grossStd x weight on every totals pass — see cart.js's
 * enrichLineItemFields / totals-refresh loop). In the normal add/edit path these
 * two are always in lockstep; TIC-9393 (SMS, KVN/113686) found one line where
 * nettotal froze at the raw per-unit rate (weight=1 equivalent) while linestd
 * correctly reflected the final weight (0.75) -- an overcharge invisible to DI-01
 * because cart.total itself was built FROM the frozen nettotal, so header and
 * line summed consistently with each other, just both wrong relative to what the
 * receipt's own per-line Amount column shows.
 *
 * Confirmed via full order-history scan of one client's till (12,961 fractional-
 * weight lines): only 1 showed nettotal > linestd (this one). The other 34
 * mismatches all had nettotal < linestd, consistent with an ordinary discount,
 * not this defect -- so this check ONLY flags the overcharge direction
 * (nettotal > linestd) to avoid false-positiving on legitimate discounted lines.
 *
 * Purely diagnostic (non-blocking, matches DI-01-warn/DI-34 philosophy) -- the
 * exact code path that leaves nettotal unscaled is not yet confirmed (every
 * traced add/edit branch computes it correctly; this is a rare timing/state
 * issue, not a reachable wrong branch), so this logs full line context to catch
 * it live next time instead of guessing at a fix.
 *
 * @param {Object} cart - Cart/order object with `items`
 * @param {string} [context=''] - Caller context for log messages
 * @param {Object} [dbAdapter=null] - When provided, durably logged (see logIntegrityViolation)
 * @returns {{ valid: boolean, flaggedLines: Array }}
 */
export function assertLineWeightScaling(cart, context = "", dbAdapter = null) {
  const items = cart.items || [];
  const flaggedLines = [];

  items.forEach((item, idx) => {
    if (item?.isCanceled === "Y" || item?.isGiftCard) return;
    const isDecimalItem = item?.uomData?.[0]?.decimal === "Y" || item?.isDecimalQty || item?.isDecimal;
    const weight = Number(item?.weight);
    if (!isDecimalItem || !Number.isFinite(weight) || weight === 0 || weight === 1) return;

    const nettotal = Number(item?.nettotal);
    const linestd = Number(item?.linestd);
    if (!Number.isFinite(nettotal) || !Number.isFinite(linestd)) return;

    const diff = nettotal - linestd;
    if (diff > TOLERANCE) {
      flaggedLines.push({
        lineIndex: idx,
        lineId: item.lineId,
        name: item.name,
        weight,
        nettotal: roundTo(nettotal, 4),
        linestd: roundTo(linestd, 4),
        diff: roundTo(diff, 4),
        listPrice: item.listPrice,
        sunitprice: item.sunitprice,
        salePrice: item.salePrice,
        taxFlag: item.taxFlag,
        isManualQty: item.isManualQty,
        upc: item.upc,
        value: item.value,
      });
    }
  });

  if (flaggedLines.length > 0) {
    logIntegrityViolation(
      "DI-37",
      {
        context,
        documentno: cart.documentno,
        sOrderID: cart.sOrderID,
        tillName: cart.tillName,
        flaggedLines,
      },
      dbAdapter
    );
    return { valid: false, flaggedLines };
  }
  return { valid: true, flaggedLines: [] };
}

/**
 * Central logging function for all data integrity violations.
 *
 * @param {string} checkId - e.g. "DI-01", "DI-02-warn"
 * @param {Object} details - Structured details of the violation
 * @param {Object} [dbAdapter=null] - BUG-1965: when provided, also mirrors the
 *   violation into the existing durable sequenceAuditLog trail (logSequenceEvent),
 *   the same mechanism DI-31/DI-32/DI-33 already use, so console-only checks like
 *   DI-01-warn become reviewable via window.__seqDiag()/__seqDiagDownload()
 *   instead of only visible in a live DevTools session. console.error is
 *   unchanged/still fires either way — this is additive, not a replacement.
 */
export function logIntegrityViolation(checkId, details, dbAdapter = null) {
  console.error(
    `[DATA-INTEGRITY] ${checkId}:`,
    JSON.stringify(details)
  );
  if (dbAdapter) {
    logSequenceEvent(checkId, details, dbAdapter);
  }
}
